import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms'
import { SearchInputClear, SelectItem } from '@ftr/forms'
import { ApiResult, DestroySubscribers, tapData } from '@ftr/foundation'
import { BehaviorSubject, Observable, combineLatest, debounceTime, switchMap, takeUntil, tap } from 'rxjs'
import { CheckboxListFilter } from '../CheckboxListFilter'
import {
  ComplexFilterData,
  ComplexFilterSearchable,
  FormValueType,
  NestingDisplayMode,
  SEARCH_DEBOUNCE,
  mapFormValueToFilter,
} from '../ComplexFilter'
import { ComplexFilterService, checkMaxSelectedOptions } from '../complex-filter.service'

@Component({
  selector: 'ftr-complex-filter-checkbox-list',
  templateUrl: './complex-filter-checkbox-list.component.html',
  styleUrls: ['./complex-filter-checkbox-list.component.css'],
})
export class ComplexFilterCheckboxListComponent extends DestroySubscribers implements OnInit {
  /**
   * Filter configuration, must be specific to the checkbox-list filter type
   */
  @Input() filter: CheckboxListFilter
  @Input() toggled: Observable<boolean>
  @Input() visibilityToggled: Observable<boolean>
  @Input() applyImmediately: boolean
  @Input() nestingDisplayMode: NestingDisplayMode
  @Output() filterChanged = new EventEmitter<ComplexFilterData>()

  internalFormGroup: UntypedFormGroup = new UntypedFormGroup({})
  searchControl: UntypedFormControl = new UntypedFormControl('')
  viewModel: ApiResult<SelectItem<string>[]>
  readonly searchInputClear = SearchInputClear
  /**
   * This value changes with the search term after debounceTime has passed.
   * It allows us to tell the difference between an empty initial state and an empty search result.
   */
  searchTerm: string | undefined

  // Active options used in the template, filtered by the search term
  filteredOptions: SelectItem<string>[] = []
  // To refresh the filter value when options get an update
  refreshFilter: BehaviorSubject<void> = new BehaviorSubject<void>(undefined)
  // To refresh filter options following a search or an API error
  refreshOptions: BehaviorSubject<void> = new BehaviorSubject<void>(undefined)

  // Keep all options to avoid extra API requests when using client-side search
  private unfilteredOptions: SelectItem<string>[] = []
  private lastValue: ComplexFilterData

  constructor(private readonly complexFilterService: ComplexFilterService) {
    super()
  }

  ngOnInit(): void {
    this.setupViewModel()
    this.setupSearchControl()
    this.listenToFilterChange()
    this.listenToVisibilityChange()
  }

  setupViewModel(): void {
    this.viewModel = this.refreshOptions.pipe(
      switchMap(() => {
        const filterValue = mapFormValueToFilter(this.internalFormGroup.value)
        return this.filter.options(this.searchControl.value, filterValue).pipe(
          tapData(options => {
            this.buildInternalFormControls(options, filterValue)
            this.refreshFilter.next(undefined)
          }),
        )
      }),
    )
  }

  setupSearchControl(): void {
    this.searchControl.valueChanges
      .pipe(takeUntil(this.finalize), debounceTime(SEARCH_DEBOUNCE))
      .subscribe(searchTerm => this.search(searchTerm))

    this.toggled.pipe(takeUntil(this.finalize)).subscribe(toggled => {
      if (!toggled) {
        this.searchControl.reset()
      }
    })
  }

  listenToFilterChange(): void {
    combineLatest([this.complexFilterService.listenToFilterChange([this.filter]), this.refreshFilter])
      .pipe(takeUntil(this.finalize))
      .subscribe(([filters]) => {
        this.internalFormGroup.reset(false, { emitEvent: false })
        // Patch the form without emitting
        this.internalFormGroup.patchValue(this.mapFilterToFormValue(filters[this.filter.filterKey] as string[]), {
          emitEvent: false,
        })
        this.lastValue = this.internalFormGroup.value

        checkMaxSelectedOptions(
          this.internalFormGroup,
          mapFormValueToFilter(this.internalFormGroup.value),
          this.filter.maxSelectedOptions,
        )
      })
  }

  getCheckboxFormControl(item: SelectItem<string>): UntypedFormControl {
    return this.internalFormGroup.controls[item.value] as UntypedFormControl
  }

  private search(searchTerm: string | undefined): void {
    this.searchTerm = searchTerm

    if (this.filter.searchable === ComplexFilterSearchable.ClientSide) {
      this.filteredOptions = this.unfilteredOptions.filter(option =>
        option.key.toLowerCase().includes((searchTerm || '').toLowerCase()),
      )
    } else {
      this.refreshOptions.next(undefined)
    }
  }

  private buildInternalFormControls(options: SelectItem<string>[], filterValue: string[]): void {
    const hiddenOptions = this.getHiddenOptions(options, filterValue)
    this.filteredOptions = [...options].concat(hiddenOptions)
    this.unfilteredOptions = [...options].concat(hiddenOptions)

    this.filteredOptions.forEach(option =>
      this.internalFormGroup.addControl(option.value, new UntypedFormControl(), { emitEvent: false }),
    )

    this.internalFormGroup.valueChanges.pipe(takeUntil(this.finalize)).subscribe(formValue => {
      const value = mapFormValueToFilter(formValue)
      checkMaxSelectedOptions(this.internalFormGroup, value, this.filter.maxSelectedOptions)
      this.filterChanged.emit({ [this.filter.filterKey]: value })
    })
  }

  /**
   * This is to ensure the value of the filter resets to the previous one
   * when we change it manually and close the modal without applying.
   */
  private listenToVisibilityChange(): void {
    this.visibilityToggled
      .pipe(
        takeUntil(this.finalize),
        tap(visible => {
          if (!visible && !this.applyImmediately) {
            this.internalFormGroup.reset(this.lastValue, { emitEvent: false })
            const filterValue = mapFormValueToFilter(this.lastValue)
            checkMaxSelectedOptions(this.internalFormGroup, filterValue, this.filter.maxSelectedOptions)
            this.filterChanged.emit({ [this.filter.filterKey]: filterValue })
          }
        }),
      )
      .subscribe()
  }

  private mapFilterToFormValue(filters: string[]): FormValueType {
    return filters.reduce((acc, val) => {
      // If there is no such control, we create it and set to true
      // It may be a hidden option or a preselected value
      if (!this.internalFormGroup.controls[val]) {
        this.internalFormGroup.addControl(val, new UntypedFormControl(), { emitEvent: false })
      }
      acc[val] = true
      return acc
    }, {} as FormValueType)
  }

  /**
   * For each filter value that does not have a valid option,
   * we attempt to find a matching hidden option or create a new option.
   * With typical use, all possible filter values should fall under options or hidden options,
   * but if the user modifies a query string param, they will still get an "option" they can deselect
   */
  private getHiddenOptions(options: SelectItem<string>[], filterValue: string[]): SelectItem<string>[] {
    // Only when the filter isn't being searched, we don't want extra options to appear in search results
    if (this.searchTerm) {
      return []
    }

    return filterValue
      .filter(f => !options.find(o => o.value === f))
      .map(f => {
        const hiddenOption = this.filter.hiddenOptions?.find(h => h.value === f)
        if (hiddenOption) {
          return hiddenOption
        }

        return {
          key: f,
          value: f,
        } as SelectItem<string>
      })
  }
}
